"use client"

import { useEffect, useRef, useState } from "react";
import Image from "next/image"

import { Button, Card, CardBody, Input, Modal, ModalContent, Textarea, useDisclosure } from "@heroui/react";
import { motion } from "framer-motion";
import { MdEmail, MdOutlineSubject } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";
import HCaptcha from '@hcaptcha/react-hcaptcha';

import logomark from '@/../public/logomark.svg'

const managementEmail = "management@nextgg.live"
const PRODUCTION = true;

export default function Contact() {
    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    const captchaRef = useRef<HCaptcha | null>(null);
    const captchaWrapperRef = useRef<HTMLDivElement | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [showCaptcha, setShowCaptcha] = useState(false);

    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState<boolean | null>(null);

    useEffect(() => {
        if (!captchaWrapperRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowCaptcha(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "200px" }
        );

        observer.observe(captchaWrapperRef.current);

        return () => observer.disconnect();
    }, []);

    const delay = (ms: number) =>
        new Promise(resolve => setTimeout(resolve, ms));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!captchaToken) {
            alert("Please complete the captcha")
            return;
        }

        setSubmitting(true)
        setSuccess(null);

        if (PRODUCTION) {
            const formData = new FormData();
            formData.append("access_key", "58b07f09-bfcb-45af-b378-7e243f95e016");
            formData.append("email", email);
            formData.append("subject", subject);
            formData.append("message", message);
            formData.append("h-captcha-response", captchaToken);

            try {
                const res = await fetch("https://api.web3forms.com/submit", {
                    method: "POST",
                    body: formData,
                });
                const data = await res.json();
                if (data.success) {
                    setSuccess(true);
                    setEmail("");
                    setSubject("");
                    setMessage("");
                    captchaRef.current?.resetCaptcha();
                    setCaptchaToken(null);
                } else {
                    setSuccess(false)
                }
            } catch (err) {
                console.error(err);
                setSuccess(false)
            } finally {
                setSubmitting(false);
                onOpen()
            }
        } else {
            await delay(1000)
            onOpen()
            setSubmitting(false);
            setSuccess(true);
            setEmail("");
            setSubject("");
            setMessage("");
        }
    };

    return (
        <>
            {/* Contact Form */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ duration: 0.8 }}
            >
                <form onSubmit={handleSubmit}>
                    <Card className="flex justify-center bg-transparent backdrop-blur-2xl p-4 mt-6 outline-1 outline-black rounded-2xl purple-glow max-w-xl mx-auto">
                        <div className="flex w-full justify-center items-center">
                            <Image
                                src={logomark}
                                alt="Logomark"
                                width={100}
                                className="drop-shadow-[0_0_10px_#8b5cf6]"
                            />
                        </div>
                        <h2 className="second-header">CONTACT US</h2>
                        <div className="flex flex-col pt-2 pb-6">
                            <p className="text-center items-center text-white">Got a question? Just want to say hi?</p>
                            <p className="text-center items-center text-white">Let us know!</p>
                        </div>
                        <CardBody>
                            <div className="flex flex-col justify-between gap-6">
                                <div className="flex flex-col w-full gap-4">
                                    <Input
                                        errorMessage="Please enter a valid email"
                                        value={email}
                                        onValueChange={setEmail}
                                        label="Email"
                                        size="lg"
                                        variant="bordered"
                                        radius="lg"
                                        placeholder="Enter your email"
                                        type="email"
                                        name="email"
                                        isRequired
                                        startContent={
                                            <MdEmail color="white" className="pointer-events-none" size={20} />
                                        }
                                        classNames={{
                                            label: ["group-data-[filled-within=true]:text-white"],
                                            input: ["bg-transparent", "text-white", "placeholder:text-white/50"],
                                            innerWrapper: "bg-transparent",
                                            inputWrapper: ["border-primary-100", "data-[hover=true]:border-primary-100", "group-data-[focus=true]:border-primary-100"]
                                        }}
                                    />
                                    <Input
                                        label="Subject"
                                        size="lg"
                                        variant="bordered"
                                        radius="lg"
                                        placeholder="Your subject"
                                        type="text"
                                        value={subject}
                                        onValueChange={setSubject}
                                        isRequired
                                        name="subject"
                                        startContent={
                                            <MdOutlineSubject color="white" className="pointer-events-none" size={20} />
                                        }
                                        classNames={{
                                            label: ["group-data-[filled-within=true]:text-white"],
                                            input: ["bg-transparent", "text-white", "placeholder:text-white/50"],
                                            innerWrapper: "bg-transparent",
                                            inputWrapper: ["border-primary-100", "data-[hover=true]:border-primary-100", "group-data-[focus=true]:border-primary-100"]
                                        }}
                                    />
                                    <Textarea
                                        label="Message"
                                        size="lg"
                                        variant="bordered"
                                        radius="lg"
                                        disableAutosize
                                        disableAnimation
                                        placeholder="Write your message"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        isRequired
                                        name="message"
                                        classNames={{
                                            label: ["group-data-[filled-within=true]:text-white"],
                                            input: ["bg-transparent", "text-white", "placeholder:text-white/50", "resize-y min-h-[40px]"],
                                            innerWrapper: "bg-transparent",
                                            inputWrapper: ["border-primary-100", "data-[hover=true]:border-primary-100", "group-data-[focus=true]:border-primary-100"]
                                        }}
                                    />
                                    <div ref={captchaWrapperRef} className="flex justify-center transition-all duration-100">
                                        {showCaptcha && (
                                            <HCaptcha
                                                sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                                                onVerify={(token) => setCaptchaToken(token)}
                                                ref={captchaRef} // for resetCaptcha()
                                            />
                                        )}
                                    </div>
                                    <Button
                                        className="bg-primary-800 hover:outline-2 hover:outline-white outline-offset-2 transition-all ease-in-out text-white w-1/3 self-center"
                                        disableRipple
                                        size="lg"
                                        color="primary"
                                        type="submit"
                                        isDisabled={submitting}
                                    >
                                        {submitting ? "Sending..." : "Submit"}
                                    </Button>
                                    <p className="text-gray-400 text-center">You can also send us an email directly at: <a className="text-blue-400" href={`mailto:${managementEmail}`}>{managementEmail}</a></p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </form>
            </motion.div>

            {/* Alert */}
            <Modal className="flex justify-center bg-transparent backdrop-blur-2xl p-4 mt-6 outline-1 outline-black rounded-2xl purple-glow max-w-md mx-auto" isOpen={isOpen} onOpenChange={onOpenChange} hideCloseButton disableAnimation>
                <ModalContent className="text-white">
                    {success ? (
                        <div className="m-4">
                            <motion.div
                                className="flex justify-center w-full text-green-600"
                                initial={{ scale: 0, y: 50 }}
                                whileInView={{ scale: 1, y: 0 }}
                                viewport={{ once: false }}
                                transition={{ duration: 0.2 }}
                            >
                                <motion.div
                                    animate={{ "--glow": ["1px", "2px", "1px"] }}
                                    style={{
                                        display: "inline-block",
                                        filter: "drop-shadow(0 0 var(--glow) #0f0) drop-shadow(0 0 calc(var(--glow)*0.5) #0f0)",
                                    }}
                                    transition={{
                                        duration: 5,
                                        repeat: Infinity,
                                        repeatType: "loop",
                                        ease: "easeInOut",
                                    }}
                                >
                                    <FaCheckCircle size={64} />
                                </motion.div>
                            </motion.div>
                            <p className="text-green-400 text-center mt-2">Message sent successfully!</p>
                            <p className="text-white text-center mt-2">We will get back to you as soon as we can!</p>
                        </div>
                    ) : (
                        <div className="m-4">

                            <motion.div
                                className="flex justify-center w-full text-red-600"
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                transition={{ duration: 0.2 }}
                            >
                                <IoMdCloseCircle size={64} />
                            </motion.div>
                            <p className="text-red-600 text-center mt-2">Error sending message</p>
                            <p className="text-white text-center mt-2">Sorry! Something went wrong! </p>
                            <p className="text-white text-center mt-2">Perhaps contacting us directly instead: <a className="text-blue-400" href={`mailto:${managementEmail}`}>{managementEmail}</a></p>
                        </div>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}